import type { UnwrapRef } from "vue";

/**
 * Representasi state loading generik untuk sebuah flow.
 *
 * `action` dipakai ketika satu form atau section memiliki lebih dari satu aksi
 * yang mungkin loading secara bergantian, misalnya `submit`, `delete`, atau `refresh`.
 */
export interface LoadingState<TActions> {
  action: TActions | null;
  state: boolean;
}

/**
 * Composable generik untuk mengelola loading state sederhana.
 *
 * Cocok dipakai di form atau flow section-level yang butuh:
 * - penanda loading boolean
 * - informasi aksi mana yang sedang berjalan
 * - helper reset yang konsisten
 *
 * @returns Objek utilitas loading yang berisi `loading`, `setLoading`, dan `resetLoading`
 */
export function useLoadingControl<TActions>() {
  const loadingRef = ref<LoadingState<TActions>>({
    action: null,
    state: false,
  });

  /**
   * Mengaktifkan atau memperbarui loading state untuk aksi tertentu.
   *
   * @param action Nama aksi yang sedang berjalan
   * @param state Nilai loading yang ingin diset
   */
  const setLoading = (
    action: UnwrapRef<TActions> | null = null,
    state: boolean = true,
  ) => {
    loadingRef.value = {
      action,
      state,
    };
  };

  /**
   * Mengembalikan loading state ke kondisi awal.
   */
  const resetLoading = () => {
    loadingRef.value = {
      action: null,
      state: false,
    };
  };

  /**
   * Snapshot reaktif dari loading state saat ini.
   */
  const loading = computed(() => loadingRef.value);

  return {
    setLoading,
    resetLoading,
    loading,
  };
}
